import { useSyncExternalStore } from "react";
import {
  TelemetryLevel,
  getBootTelemetryLevel,
  getCachedKillSwitch,
  getTelemetryLevel,
  setTelemetryLevel,
} from "../utils/telemetryLevel";

/**
 * Telemetry state as reactive module state (DHT-5279).
 *
 * Two slots:
 *
 * - The DEVICE level, what this machine is set to, owned by the settings
 *   screen.
 * - The EFFECTIVE level, the device level combined with LaunchDarkly's kill
 *   switch. Published by `useTelemetryGate` and read by everything else.
 *
 * The publish/subscribe split exists because of where the consumers are
 * mounted. `AppAnalyticsProvider` sits six levels ABOVE `FeatureFlagProvider`
 * in ClinicApp (`App.tsx:221` vs `:227`), and `GoogleTagManagerProvider` wraps
 * it outright in PatientApp. A hook reading LaunchDarkly from either of those
 * resolves to nothing at all and silently falls back, which is the trap the
 * comment at `PatientApp.tsx:180` already warns about. Only the gate reads
 * LaunchDarkly, from inside the provider where that works, and publishes here;
 * everyone else subscribes and has no ordering requirement.
 *
 * Deliberately NOT Recoil: the two apps compose their providers differently and
 * this has to work wherever it is mounted, which is the same problem again.
 */
let cachedLevel: TelemetryLevel | undefined;
const deviceListeners = new Set<() => void>();

const readDevice = (): TelemetryLevel => {
  if (cachedLevel === undefined) cachedLevel = getTelemetryLevel();
  return cachedLevel;
};

const subscribeDevice = (listener: () => void): (() => void) => {
  deviceListeners.add(listener);
  return () => {
    deviceListeners.delete(listener);
  };
};

/**
 * What `initTelemetry` actually configured, captured before anything can change
 * it, and the two inputs it came from.
 *
 * The gate needs `bootTelemetryLevel` to know whether Session Replay is
 * resumable in the current session: the replay sampling decision is drawn once
 * and stored in the session cookie, so a session that booted at `noReplay`
 * cannot record again without forcing, and forcing would override sampling for
 * everyone if the sample rate is ever lowered. See `applyTelemetryLevel`.
 *
 * It needs `bootKillSwitch` so that, before LaunchDarkly answers, the gate
 * agrees with the SDKs rather than computing a different level from a fallback.
 */
export const bootTelemetryLevel: TelemetryLevel = getBootTelemetryLevel();
export const bootKillSwitch: boolean = getCachedKillSwitch();

/** Persists the device level and notifies subscribers. */
export const setDeviceTelemetryLevel = (level: TelemetryLevel): void => {
  if (readDevice() === level) return;
  cachedLevel = level;
  setTelemetryLevel(level);
  deviceListeners.forEach(listener => listener());
};

/** Subscribes to this device's own level. The settings dropdown shows this. */
export const useDeviceTelemetryLevel = (): TelemetryLevel =>
  useSyncExternalStore(subscribeDevice, readDevice, readDevice);

/**
 * The effective level, seeded with what the SDKs were booted with so every
 * subscriber agrees with reality before LaunchDarkly has answered.
 */
let effectiveLevel: TelemetryLevel = bootTelemetryLevel;
const effectiveListeners = new Set<() => void>();

const readEffective = (): TelemetryLevel => effectiveLevel;

const subscribeEffective = (listener: () => void): (() => void) => {
  effectiveListeners.add(listener);
  return () => {
    effectiveListeners.delete(listener);
  };
};

/**
 * Publishes the effective level. Only `useTelemetryGate` should call
 * this: it is the one hook mounted where LaunchDarkly is readable.
 */
export const publishEffectiveTelemetryLevel = (level: TelemetryLevel): void => {
  if (effectiveLevel === level) return;
  effectiveLevel = level;
  effectiveListeners.forEach(listener => listener());
};

/**
 * The effective level, read outside React. Used by the Datadog consent sync
 * (`adapters/init.web.ts`), which re-applies the level when the visitor's tracking
 * consent changes.
 */
export const getEffectiveTelemetryLevel = (): TelemetryLevel => effectiveLevel;

/** Subscribes to the effective level. Safe to call from anywhere. */
export const useEffectiveTelemetryLevelValue = (): TelemetryLevel =>
  useSyncExternalStore(subscribeEffective, readEffective, readEffective);
